import React, { useEffect, useRef } from "react";
import styles from "./BrandPromise.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLeaf, faAward, faTruck } from "@fortawesome/free-solid-svg-icons";
import { motion, useAnimation, Variants } from "framer-motion";

const BrandPromise: React.FC = () => {
  const controls = useAnimation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          controls.start("visible");
        }
      },
      {
        threshold: 0.1,
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [controls]);

  const promises = [
    {
      icon: faLeaf,
      title: "Sustainable Craftsmanship",
      description:
        "We use eco-friendly materials and processes to create furniture that's kind to the planet without compromising on quality.",
      theme: "eco",
    },
    {
      icon: faAward,
      title: "Uncompromising Quality",
      description:
        "Every piece undergoes rigorous quality checks to ensure it meets our high standards for durability and finish.",
      theme: "premium",
    },
    {
      icon: faTruck,
      title: "Nationwide Delivery",
      description:
        "We deliver and install your furniture anywhere in the country with our professional logistics network.",
      theme: "delivery",
    },
  ];

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const getHoverShadow = (theme: string) => {
    switch (theme) {
      case "eco":
        return "0 15px 30px rgba(76, 175, 80, 0.2)";
      case "premium":
        return "0 15px 30px rgba(245, 175, 25, 0.2)";
      case "delivery":
        return "0 15px 30px rgba(33, 150, 243, 0.2)";
      default:
        return "0 15px 30px rgba(245, 175, 25, 0.2)";
    }
  };

  return (
    <section className={styles.promiseSection} ref={ref}>
      <motion.div
        className={styles.sectionHeader}
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <motion.h2 className={styles.sectionTitle} variants={itemVariants}>
          The SCADEL Promise
        </motion.h2>
        <motion.div className={styles.gradientBar} variants={itemVariants} />
        <motion.p className={styles.sectionSubtitle} variants={itemVariants}>
          Why commercial businesses across India choose our furniture solutions
        </motion.p>
      </motion.div>

      <motion.div
        className={styles.promiseGrid}
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        {promises.map((promise, index) => (
          <motion.div
            key={index}
            className={`${styles.promiseCard} ${
              styles[`${promise.theme}Card`]
            }`}
            variants={itemVariants}
            whileHover={{
              y: -10,
              boxShadow: getHoverShadow(promise.theme),
            }}
          >
            <div
              className={`${styles.iconWrapper} ${
                styles[`${promise.theme}Icon`]
              }`}
            >
              <FontAwesomeIcon icon={promise.icon} className={styles.icon} />
            </div>
            <h3 className={styles.promiseTitle}>{promise.title}</h3>
            <p className={styles.promiseDescription}>{promise.description}</p>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

export default BrandPromise;
